import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { CheckboxComponent } from '@ps-gui/ui';
import {
  ButtonDirective,
  classByKey,
  DocumentClassKey,
  FloatingMenuComponent,
  FloatingMenuItemComponent,
  MaterialIconComponent,
  NativeElementHook,
} from '@ps-gui/shared-core';
import { BehandlerItem } from '../../model/behandler-item.interface';

export const NONE_MEDICAL_BEHANDLER_ITEM: BehandlerItem = {
  id: '@NONE_MEDICAL@',
  displayName: 'Nicht medizinisch',
};

@Component({
  imports: [
    ButtonDirective,
    FloatingMenuComponent,
    FloatingMenuItemComponent,
    MaterialIconComponent,
    NativeElementHook,
    NgOptimizedImage,
    NgClass,
    CheckboxComponent,
  ],
  selector: 'shared-dp-document-class-filter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './document-filter.component.html',
  styleUrl: './document-filter.component.scss',
})
export class DocumentFilterComponent {
  readonly availableDocumentClasses = input.required<string[]>();
  readonly availableBehandlers = input.required<BehandlerItem[]>();
  readonly growDirection = input<'right' | 'left'>('right');

  readonly appliedDocumentClassesChanged = output<string[]>();
  readonly appliedBehandlersChanged = output<BehandlerItem[]>();

  protected readonly crossOffset = computed(() => (this.growDirection() === 'right' ? 0 : -250));

  protected readonly documentClassCheckboxes = computed(() => {
    return this.availableDocumentClasses().map((documentClass: string) => ({
      text: classByKey[documentClass as DocumentClassKey].label,
      value: documentClass,
      checked: signal(this.selectedDocumentClasses().includes(documentClass)),
    }));
  });

  protected readonly behandlerCheckboxes = computed(() => {
    return this.availableBehandlers().map((behandler) => ({
      text: behandler.displayName,
      value: behandler,
      checked: signal(this.selectedBehandlers().includes(behandler)),
    }));
  });

  protected readonly selectedDocumentClasses = linkedSignal<string[]>(() =>
    this.availableDocumentClasses(),
  );

  protected readonly selectedBehandlers = linkedSignal<BehandlerItem[]>(() =>
    this.availableBehandlers(),
  );

  protected readonly anyFilterApplied = computed(
    () =>
      this.availableDocumentClasses().some(
        (documentClass) => !this.selectedDocumentClasses().includes(documentClass),
      ) ||
      this.availableBehandlers().some(
        (behandler) => !this.selectedBehandlers().includes(behandler),
      ),
  );

  protected readonly isOpen = signal(false);

  protected toggleFilterMenu(event: MouseEvent) {
    event.stopPropagation();
    this.isOpen.update((bool) => !bool);
  }

  protected onApplyFilter() {
    this.selectedDocumentClasses.set(
      this.documentClassCheckboxes()
        .filter((checkbox) => checkbox.checked())
        .map((checkbox) => checkbox.value),
    );
    this.selectedBehandlers.set(
      this.behandlerCheckboxes()
        .filter((checkbox) => checkbox.checked())
        .map((checkbox) => checkbox.value),
    );

    this.appliedDocumentClassesChanged.emit(this.selectedDocumentClasses());
    this.appliedBehandlersChanged.emit(this.selectedBehandlers());
    this.isOpen.set(false);
  }

  protected onResetFilterClicked() {
    this.documentClassCheckboxes().forEach((checkbox) => checkbox.checked.set(true));
    this.behandlerCheckboxes().forEach((checkbox) => checkbox.checked.set(true));
    this.onApplyFilter();
  }

  protected hide() {
    this.isOpen.set(false);
  }
}
